import { EditorState, strCompareLowerCase } from "@common/Common";
import type { ValidationError } from "@common/types";
import type { RawCriterionWeight } from "@Criteria/Model";
import type { CriterionWeightReadModel, WeightsReadModel, WeightsSetEditorReadModel } from "@Criteria/Weights/Domain/readmodels";

// --------------------------------------------
// Flat criterion shape
// --------------------------------------------

export type WeightsSetEditorCriterion = {
    id: number;
    name: string;
    path: string;
    isGoal: boolean;
    inputValue: number | null;
    localWeight: number | null;
    globalWeight: number | null;
};

// --------------------------------------------
// Main ViewModel
// --------------------------------------------

export class WeightsSetEditorViewModel
{
    editorState: EditorState = EditorState.Closed;
    initialized = false;

    weightsSetId: number | null = null;
    name = '';

    criteria: WeightsSetEditorCriterion[] = [];
    errors: ValidationError[] = [];

    showCreator(name: string)
    {
        this.initialized = false;
        this.weightsSetId = null;
        this.name = name;
        this.criteria = [];
        this.errors = [];
        this.editorState = EditorState.Creator;
    }

    showEditor(weightsSetId: number, name: string)
    {
        this.initialized = false;
        this.weightsSetId = weightsSetId;
        this.name = name;
        this.criteria = [];
        this.errors = [];
        this.editorState = EditorState.Editor;
    }

    close()
    {
        this.editorState = EditorState.Closed;
        this.initialized = false;
        this.weightsSetId = null;
        this.name = '';
        this.criteria = [];
        this.errors = [];
    }

    setData(data: WeightsSetEditorReadModel)
    {
        this.criteria = data.Weights
            .map(w => ({
                id: w.CriterionId,
                name: w.Name,
                path: w.Path,
                isGoal: w.ParentId == null,
                inputValue: w.LocalWeightPercent,
                localWeight: w.LocalWeightPercent,
                globalWeight: w.GlobalWeightPercent
            }))
            .sort((a, b) => strCompareLowerCase(a.path, b.path));

        this.initialized = true;
    }

    setRecalculatedWeights(rm: WeightsReadModel)
    {
        for (const curr of this.criteria)
        {
            const incoming = rm.Weights.find(w => w.CriterionId === curr.id)!;

            curr.globalWeight = incoming.GlobalWeightPercent;
            curr.inputValue = incoming.LocalWeightPercent;
            curr.localWeight = incoming.LocalWeightPercent;
        }
    }

    displayValidationErrors(errors: ValidationError[])
    {
        this.errors = errors;
    }
}


export class WeightsSetEditorCreateWeightsSetEventPayload
{
    name: string;
    weights: RawCriterionWeight[];

    constructor(name: string, weights: RawCriterionWeight[])
    {
        this.name = name;
        this.weights = weights;
    }
}

export class WeightsSetEditorUpdateWeightsSetEventPayload
{
    weightsSetId: number;
    name: string;
    weights: RawCriterionWeight[];

    constructor(weightsSetId: number, name: string, weights: RawCriterionWeight[])
    {
        this.weightsSetId = weightsSetId;
        this.name = name;
        this.weights = weights;
    }
}

export class WeightsSetEditorNormalizeWeightsEventPayload
{
    weights: RawCriterionWeight[];

    constructor(weights: RawCriterionWeight[])
    {
        this.weights = weights;
    }
}